'use client'

import clsx from 'clsx'
import { useTranslation } from 'react-i18next'
import Image from 'next/image'
import pkg from '@/package.json'
import { ROUTES } from '@constants/routes'
import { usePathname } from 'next/navigation'
import { BrandSelector } from './BrandSelector'
import { useDevMode } from '@components/developer/DevModeActivator'
import { useStyleSchema } from '@components/styleSchema/StyleSchemaHandler'

interface FooterProps {
  showPolicy?: boolean
  showImprint?: boolean
}
const Footer: React.FC<FooterProps> = ({
  showPolicy = true,
  showImprint = true,
}) => {
  const { t } = useTranslation()
  const isHome = usePathname() === ROUTES.root
  const { currentSchema } = useStyleSchema();
  const logoConfig = currentSchema.logo.footer;

  return (
    <>
      {isHome && (
        <>
          <div className='container mx-auto flex items-center gap-4 justify-center bg-[--color-blue-gray-25] w-full p-4 min-h-[25dvh]'>
            <div className='flex flex-col gap-4 items-end'>
              <h3 className='text-4xl font-exosoft md:text-5xl' style={{ color: currentSchema?.colors?.accent?.hex }}>
                {t('hero.title')}
              </h3>
              <h3 className='text-4xl font-bold font-poppins md:text-5xl'>
                <span style={{ color: currentSchema?.colors?.accent?.hex }}>{t('hero.subtitle')}</span>{' '}
                <span style={{ color: currentSchema?.colors?.accent?.hex }}>
                    {t('hero.subtitle2')}
                  </span>
              </h3>
            </div>
          </div>
          <div className='container mx-auto bg-white p-4'>
            <div className='flex flex-col gap-4'>
              <h6 className='text-sm'>{t('root.footnote')}</h6>
            </div>
          </div>
        </>
      )}
      <footer
        className={clsx(
          'container mx-auto p-4 flex flex-col gap-2 text-sm font-normal font-exosoft bg-white dark:bg-[--dark-color-background-secondary]'
        )}
      >
        <div className={`flex ${logoConfig.position?.x === 'center' ? 'justify-center' : 
                logoConfig.position?.x === 'right' ? 'justify-end' : 'justify-start'} p-0`}>
          <Image
            src={logoConfig.path}
            alt={`${currentSchema.title} Logo`}
            width={Number.parseInt(logoConfig.size?.width || '200')}
            height={logoConfig.size?.height === 'auto' ? 48 : Number.parseInt(logoConfig.size?.height || '48')}
            className="max-w-full min-w-[1rem] min-h-[1rem]"
            style={{
              maxWidth: logoConfig.size?.width || 'auto',
              width: logoConfig.size?.width || 'auto',
              height: logoConfig.size?.height || 'auto'
            }}
            unoptimized={logoConfig.path.endsWith('.svg')}
          />
        </div>
        <ul className='flex items-center gap-1'>
          {showPolicy && (
            <li>
              <a
                href='https://www.klier.de/datenschutz'
                className='hover:text-[var(--color-blue-info-400)]'
                target='_blank'
                rel='noopener noreferrer'
              >
                {t('navigation.policy')}
              </a>
            </li>
          )}
          {showImprint && (
            <li>
              <a
                href='https://www.klier.de/impressum'
                className='hover:text-[var(--color-blue-info-400)]'
                target='_blank'
                rel='noopener noreferrer'
              >
                {t('navigation.imprint')}
              </a>
            </li>
          )}
        </ul>
        <span className='flex items-center gap-1'>
          &copy; {new Date().getFullYear()} KLIER Hair Group
        </span>
        {useDevMode().isDevModeActive && <div className='flex items-center gap-4 flex-wrap'>
          <span className='flex items-center gap-1'>
            Version: {pkg.version}
          </span>
          <BrandSelector />
        </div>}
      </footer>
    </>
  )
}
export default Footer
